/**
 * Die Typen des Regelkerns.
 *
 * Der Kern kennt keine Darstellung: keine Farben auf dem Bildschirm, keine
 * Bildschirmpunkte, kein Three.js. Er kennt Konten, Besitz, Eingaenge und Phasen.
 * Alles, was hier steht, laesst sich ohne Oberflaeche pruefen.
 */
import type { HotelId } from './data/hotels'
import type { Zufall } from './rng'
import type { Bauwuerfel } from './rng'

export type SpielerId = number

/** Was mit einem Grundstueck passiert ist, sobald es jemandem gehoert. */
export interface Besitz {
  readonly hotel: HotelId
  readonly besitzer: SpielerId
  /** Zahl der errichteten Gebaeude. 0 heisst: gekauft, aber unbebaut. */
  readonly gebaeude: number
  readonly freizeitpark: boolean
  /** Runde des Erwerbs. Gebaut werden darf erst in der Runde danach. */
  readonly erworbenInRunde: number
  /**
   * Runde, in der das letzte Gebaeude fertig wurde. Der Freizeitpark darf laut
   * Anleitung erst "in jeder beliebigen der nachfolgenden Runden" dazukommen,
   * also nicht im selben Zug. `null`, solange das Hotel unvollstaendig ist.
   */
  readonly komplettSeitRunde: number | null
}

/** Ein Hoteleingang an einem Strassenfeld. Je Feld hoechstens einer. */
export interface Eingang {
  readonly feld: number
  readonly hotel: HotelId
}

export interface Spieler {
  readonly id: SpielerId
  readonly name: string
  readonly farbe: string
  readonly bot: boolean
  readonly konto: number
  readonly feld: number
  readonly ausgeschieden: boolean
}

export interface Regeln {
  readonly startkapital: number
  readonly bankpassage: number
  /**
   * Was passiert, wenn das Zielfeld besetzt ist. Die deutsche Referenzanleitung
   * sagt "auf das naechste freie Feld davor", die englische Fassung "ahead".
   * Voreinstellung ist die Referenz.
   */
  readonly blockierteFelder: 'zurueck' | 'vor'
  /**
   * Hausregel gegen endlose Partien: nach so vielen Runden gewinnt das hoechste
   * Gesamtvermoegen. `null` schaltet sie ab. Siehe offene-fragen.md O5.
   */
  readonly rundenlimit: number | null
  /** Zahl der Hoteleingaenge im Vorrat. */
  readonly eingaengeVorrat: number
}

export const STANDARDREGELN: Regeln = {
  startkapital: 12000,
  bankpassage: 2000,
  blockierteFelder: 'zurueck',
  rundenlimit: 300,
  eingaengeVorrat: 30,
}

/** Rechte und Beschraenkungen, die nur fuer den laufenden Zug gelten. */
export interface Zugrechte {
  /** Rathaus passiert: je Hotel mit Hauptgebaeude darf ein Eingang gekauft werden. */
  readonly rathaus: boolean
  /** Hotels, die dieses Recht in diesem Zug schon genutzt haben. */
  readonly rathausGenutzt: readonly HotelId[]
  /** Gratis-Eingaenge aus dem Feld "Ein Eingang kostenlos". */
  readonly gratisEingaenge: number
  /** Gratis-Bauphasen aus dem Feld "Eine Bauphase kostenlos". */
  readonly gratisBauphasen: number
  /** Auf einem Bau-Feld gelandet und noch keine Genehmigung beantragt. */
  readonly darfGenehmigungBeantragen: boolean
  /** Hoechstens eine Besitzkarte pro Zug. */
  readonly grundstueckGekauft: boolean
  /** Eine 6 gewuerfelt: nach diesem Zug folgt ein weiterer. */
  readonly nochmal: boolean
}

export const KEINE_RECHTE: Zugrechte = {
  rathaus: false,
  rathausGenutzt: [],
  gratisEingaenge: 0,
  gratisBauphasen: 0,
  darfGenehmigungBeantragen: false,
  grundstueckGekauft: false,
  nochmal: false,
}

/** Eine offene Forderung, die der Spieler am Zug begleichen muss. */
export interface Schuld {
  readonly betrag: number
  /** Empfaenger: ein Mitspieler oder die Bank. */
  readonly an: SpielerId | 'bank'
  readonly grund: string
}

export interface Auktion {
  readonly hotel: HotelId
  readonly verkaeufer: SpielerId
  readonly hoechstgebot: number
  readonly hoechstbieter: SpielerId | null
  /** Wer gerade bieten darf. */
  readonly amZug: SpielerId
  /** Wer schon ausgestiegen ist. */
  readonly ausgestiegen: readonly SpielerId[]
}

export type Phase =
  | 'wuerfeln'
  | 'uebernachten'
  | 'handeln'
  | 'schuld'
  | 'auktion'
  | 'ende'

/**
 * Was zuletzt passiert ist - fuer Wuerfelanzeige, Rechnungsblatt und die
 * Fahrtanimation. Das gehoert in den Zustand und nicht in die Oberflaeche: es ist
 * Teil dessen, was der Zug bewirkt hat, und muss eine Wiederholung ueberstehen.
 * Wird zu Beginn jedes Zuges zurueckgesetzt.
 */
export interface Anzeige {
  /** Letzter Standardwuerfelwurf zum Ziehen. */
  readonly wurf: number | null
  /** Gewuerfelte Naechte der letzten Uebernachtung. */
  readonly naechte: number | null
  /** Letzte Seite des Spezialwuerfels. */
  readonly bauwurf: Bauwuerfel | null
  /** Die zuletzt gestellte Rechnung, ausgeschrieben. */
  readonly rechnung: {
    readonly hotel: HotelId
    readonly naechte: number
    readonly satz: number
    readonly betrag: number
    readonly an: SpielerId
  } | null
  /** Die Felder, ueber die das Auto gefahren ist, Zielfeld zuletzt. */
  readonly weg: readonly number[]
}

export const LEERE_ANZEIGE: Anzeige = {
  wurf: null,
  naechte: null,
  bauwurf: null,
  rechnung: null,
  weg: [],
}

export interface Zustand {
  readonly regeln: Regeln
  readonly spieler: readonly Spieler[]
  readonly amZug: SpielerId
  readonly runde: number
  /** Kontostand der Bank. Darf negativ werden - die Bank geht nie pleite. */
  readonly bank: number
  readonly besitz: Partial<Record<HotelId, Besitz>>
  readonly eingaenge: readonly Eingang[]
  readonly rechte: Zugrechte
  readonly phase: Phase
  readonly schuld: Schuld | null
  readonly auktion: Auktion | null
  /** Zahl der Naechte der offenen Uebernachtung, sobald gewuerfelt. */
  readonly offeneRechnung: {
    readonly hotel: HotelId
    readonly naechte: number
    readonly betrag: number
  } | null
  /** Bau, fuer den die Genehmigung erteilt, aber noch nicht bezahlt ist. */
  readonly offenerBau: { readonly hotel: HotelId; readonly anzahl: number } | null
  readonly anzeige: Anzeige
  readonly zufall: Zufall
  readonly protokoll: readonly Eintrag[]
  readonly sieger: SpielerId | null
}

export interface Eintrag {
  readonly runde: number
  readonly spieler: SpielerId | null
  readonly text: string
}

export type Aktion =
  | { readonly art: 'wuerfeln' }
  | { readonly art: 'uebernachten' }
  | { readonly art: 'grundstueck-kaufen'; readonly hotel: HotelId }
  | { readonly art: 'zwangskauf'; readonly hotel: HotelId }
  | {
      readonly art: 'baugenehmigung'
      readonly hotel: HotelId
      /** Wie viele Gebaeude in einem Zug errichtet werden sollen. */
      readonly anzahl: number
    }
  | { readonly art: 'gratis-bauen'; readonly hotel: HotelId }
  | { readonly art: 'gratis-freizeitpark'; readonly hotel: HotelId }
  | { readonly art: 'eingang-kaufen'; readonly hotel: HotelId; readonly feld: number }
  | { readonly art: 'gratis-eingang'; readonly hotel: HotelId; readonly feld: number }
  | { readonly art: 'freizeitpark-kaufen'; readonly hotel: HotelId }
  | { readonly art: 'versteigern'; readonly hotel: HotelId }
  | { readonly art: 'bieten'; readonly betrag: number }
  | { readonly art: 'passen' }
  | { readonly art: 'zug-beenden' }
  /** Nach einer 6 auf den Zusatzzug verzichten. */
  | { readonly art: 'zug-abgeben' }

/** Was ein Wurf des Bauwuerfels bewirkt hat - fuer Anzeige und Protokoll. */
export interface Bauergebnis {
  readonly seite: Bauwuerfel
  readonly bezahlt: number
  readonly gebaut: number
}

export type { HotelId }
